import { Resp } from '@paradigm/api/src';

import { retryExecutor } from '@paradigm/utils/src/retry';
import { logException } from '@paradigm/logging/src/logging';
import * as ApiRepository from '#/unified-rfqs/repositories/grfq-api';
import GrfqQuotesStore from '#/unified-rfqs/store/grfq-quotes';
import RfqsStore from '#/unified-rfqs/store/rfqs';
import {
  ListRfqsReq,
  ListTradesReq,
  CreateQuoteReq,
  CancelQuoteReq,
  CreateRfqReq,
  PricingReq,
  GetLastOwnQuoteReq,
  GetTradeDetailsReq,
  ListInstrumentsReq,
  CancelRfqQuotesBatchReq,
  CancelQuotesBatchReq,
  GetRfqReq,
} from '#/unified-rfqs/repositories/grfq-types';
import OrdersStore from '#/unified-rfqs/store/orders';
import TradesStore from '#/unified-rfqs/store/trades';
import TradeTapeStore from '#/unified-rfqs/store/trade-tape';
import {
  CallbackHandlers,
  createGrfqWsService,
  GrfqWsService,
} from '#/unified-rfqs/services/grfq-ws';
import {
  WebSocketStatusHandler,
  createFailedResp,
} from '#/unified-rfqs/services/shared';
import { GrfqQuote } from '#/unified-rfqs/entities/grfq/domain';
import { isOBV1APIEnabled } from '#/feature-flags/utils';

interface GrfqServiceDeps {
  api: typeof ApiRepository;
  rfqStore: RfqsStore;
  tradesStore: TradesStore;
  tradeTapeStore: TradeTapeStore;
  ordersStore: OrdersStore;
  grfqQuoteStore: GrfqQuotesStore;
  wsService: GrfqWsService;
  handlers: CallbackHandlers;
}

type ReadonlyGrfqQuotesStore = Pick<
  GrfqQuotesStore,
  'selectRfqQuoteById' | 'selectRfqQuotes' | 'subscribeRfqQuotes'
>;
export class GrfqService implements ReadonlyGrfqQuotesStore {
  constructor(private deps: GrfqServiceDeps) {}

  setup() {
    const unsubscribe = this.deps.wsService.startWebSocket();

    return unsubscribe;
  }

  teardown() {
    this.deps.wsService.close();
  }

  public selectRfqQuoteById: ReadonlyGrfqQuotesStore['selectRfqQuoteById'] = (
    ...args
  ) => this.deps.grfqQuoteStore.selectRfqQuoteById(...args);

  public selectRfqQuotes: ReadonlyGrfqQuotesStore['selectRfqQuotes'] = (
    ...args
  ) => this.deps.grfqQuoteStore.selectRfqQuotes(...args);

  public subscribeRfqQuotes: ReadonlyGrfqQuotesStore['subscribeRfqQuotes'] = (
    ...args
  ) => this.deps.grfqQuoteStore.subscribeRfqQuotes(...args);

  subscribeWebSocketStatus(handler: WebSocketStatusHandler) {
    return this.deps.wsService.subscribeWebSocketStatus(handler);
  }

  listGrfqs = async (req: ListRfqsReq) => {
    const { api, rfqStore } = this.deps;
    const resp = await api.listRfqs(req);

    return this.handleResp(resp, (rfqs) => rfqStore.upsertMany(rfqs.results));
  };

  listTrades = async (req: ListTradesReq) => {
    const { api, tradesStore } = this.deps;
    const resp = await api.listTrades({
      ...req,
      hidePublic: true,
      status: ['COMPLETED', 'REJECTED'],
    });

    return this.handleResp(resp, (trades) =>
      tradesStore.addMany(trades.results),
    );
  };

  listTradeTape = async (req: ListTradesReq) => {
    const { api, tradeTapeStore } = this.deps;
    const resp = await api.listTradeTape(req);

    return this.handleResp(resp, (trades) =>
      tradeTapeStore.addMany(trades.results),
    );
  };

  listInstruments = async (req: ListInstrumentsReq) => {
    // WIP: create an instrument store
    const resp = await this.deps.api.listInstruments(req);
    return this.handleResp(resp);
  };

  killQuote = async (req: CancelQuoteReq) => {
    const { api, grfqQuoteStore, ordersStore } = this.deps;
    const { quoteId } = req;
    const quote = ordersStore.selectById(quoteId);

    if (quote == null) return createFailedResp('Quote not found locally');
    if (quote.rfqType !== 'grfq') return createFailedResp('Invalid quote type');

    const resp = await api.cancelQuote(req);

    if (isOBV1APIEnabled()) {
      return this.handleResp(resp); // store will be updated by WS events
    }
    return this.handleResp(resp, () => grfqQuoteStore.removeQuote(quote));
  };

  getRfq = async (req: GetRfqReq) => {
    const { api, rfqStore } = this.deps;
    const resp = await api.getRfq(req);

    return this.handleResp(resp, (rfq) => rfqStore.upsertMany([rfq]));
  };

  createRfq = async (req: CreateRfqReq) => {
    const { api, rfqStore } = this.deps;
    const resp = await api.createRfq(req);

    return this.handleResp(resp, (rfq) => {
      rfqStore.upsertMany([rfq]);
    });
  };

  createQuote = async (req: CreateQuoteReq) => {
    const { api } = this.deps;
    const resp = await api.createOrReplaceQuote(req);

    // store will be updated by WS events
    return this.handleResp(resp);
  };

  getLegPricing = async (req: PricingReq) => {
    const resp = await this.deps.api.getLegPricing(req);
    return this.handleResp(resp);
  };

  getLastOwnSideQuote = async (req: GetLastOwnQuoteReq) => {
    const resp = await this.deps.api.getLastOwnQuote(req);
    return this.handleResp(resp);
  };

  getTrade = async (req: GetTradeDetailsReq) => {
    const resp = await this.deps.api.getTrade(req);
    return this.handleResp(resp);
  };

  listRfqQuotes: typeof ApiRepository.listRfqQuotes = async (req) => {
    const { rfqStore, api, grfqQuoteStore } = this.deps;
    const { rfqId } = req;

    grfqQuoteStore.clearRfqQuotes(rfqId);
    const { value } = await retryExecutor(async () => {
      const resp = await api.listRfqQuotes(req);

      if (!resp.ok) {
        const isAborted = () => req.signal?.aborted === true;
        const wasThrottled = resp.status === 429;
        const { retryAfter } = resp.data;

        if (wasThrottled && typeof retryAfter === 'number' && !isAborted()) {
          await new Promise((resolve) =>
            setTimeout(resolve, retryAfter * 1000),
          );
        }

        const isRetriable =
          !isAborted() &&
          (wasThrottled ||
            resp.status === 0 /* Unknown network error */ ||
            resp.status >= 500); /* Unexpected server error */

        return { ok: false, retriable: isRetriable, value: resp };
      }

      const rfq = rfqStore.selectById(rfqId);

      /**
       * Complete rfq quotes information by adding description and product codes
       */
      const productCodes = rfq?.type === 'grfq' ? rfq.product_codes : [];
      const description = rfq?.description ?? '';
      const addDescriptionAndCode = (quote: GrfqQuote) =>
        ({
          ...quote,
          description,
          product_codes: productCodes,
        } as GrfqQuote);

      const asks = resp.data.asks.map(addDescriptionAndCode);
      const bids = resp.data.bids.map(addDescriptionAndCode);
      grfqQuoteStore.upsertSideQuotes({ bids, asks, id: rfqId });
      return { ok: true, value: resp };
    }, RETRY_CONFIG);

    return this.handleResp(value);
  };

  listOwnQuotes: typeof ApiRepository.listOwnQuotes = async (req) => {
    const { api, ordersStore } = this.deps;
    const resp = await api.listOwnQuotes(req);

    return this.handleResp(resp, (data) =>
      ordersStore.upsertMany(data.results),
    );
  };

  cancelQuotesBatch = async (req: CancelQuotesBatchReq) => {
    // store will be updated by WS events
    const resp = await this.deps.api.cancelQuotesBatch(req);
    return this.handleResp(resp);
  };

  cancelRfqQuotesBatch = async (req: CancelRfqQuotesBatchReq) => {
    // store will be updated by WS events
    const resp = await this.deps.api.cancelRfqQuotesBatch(req);
    return this.handleResp(resp);
  };

  private handleResp<T>(resp: Resp<T>, save?: (data: T) => void) {
    const { api } = this.deps;
    if (resp.ok) {
      save?.(resp.data);
      return resp;
    }

    logException(...api.processGrfqException(resp));
    return { ...resp, message: api.humanizeGrfqErrorMessage(resp) };
  }

  // @TODO: workaround until we have a definitive solution for localized states
  get tradeTapeStore() {
    return this.deps.tradeTapeStore;
  }
}

export function createGrfqService(
  deps: Omit<GrfqServiceDeps, 'api' | 'wsService'>,
): GrfqService {
  const api = ApiRepository;
  const wsService = createGrfqWsService(deps);
  return new GrfqService({
    ...deps,
    api,
    wsService,
  });
}

const RETRY_CONFIG = {
  retries: 10,
  factor: 1.5,
  minTimeout: 500,
  randomize: true,
};
